import { useMemo } from "react";
import { AgGrid } from "@jsq/ds/core";
import { useGetInvestmentEntitiesLazyQuery } from "../../../../graphql/supergraphHooks";
import { COMPLIANCE_PROGRAMS } from "../../constants";
import type { AccountObjectTypeSortEnum } from "../../../../graphql/supergraphTypes";

type SetFilterModel = { filterType: "set"; values: string[] | null };

const ENTITY_NAME_SORT_MAP: Record<string, AccountObjectTypeSortEnum> = {
  asc: "NAME_ASC",
  desc: "NAME_DESC",
};

export function useEntityDatasource(arenaId: number): AgGrid.IServerSideDatasource {
  const [executeEntities] = useGetInvestmentEntitiesLazyQuery();

  return useMemo((): AgGrid.IServerSideDatasource => {
    const cursorMap = new Map<number, string | null>([[0, null]]);

    return {
      getRows: (params: AgGrid.IServerSideGetRowsParams) => {
        const { startRow = 0, endRow = 20 } = params.request;

        const isNewDataset = startRow === 0;
        if (isNewDataset) {
          cursorMap.clear();
          cursorMap.set(0, null);
        }

        const numberOfRecords = endRow - startRow;
        const after = cursorMap.get(startRow) ?? null;

        const filterModel = params.request.filterModel as Record<string, SetFilterModel> | null;
        const setFilter = filterModel?.["entityName"];
        const entities = setFilter?.values?.length ? setFilter.values : undefined;

        const entityNameSortModel = params.request.sortModel.find(
          (s) => s.colId === "entityName",
        );
        const sort = entityNameSortModel
          ? [ENTITY_NAME_SORT_MAP[entityNameSortModel.sort]]
          : undefined;

        void (async () => {
          try {
            const entitiesResult = await executeEntities({
              variables: {
                arenaId,
                first: numberOfRecords,
                after,
                entities,
                sort,
                compliancePrograms: COMPLIANCE_PROGRAMS,
              },
              fetchPolicy: "network-only",
            });
            const entitiesData = entitiesResult.data;
            if (!entitiesData) {
              params.fail();
              return;
            }

            const endCursor = entitiesData.investmentEntities?.pageInfo?.endCursor ?? null;
            cursorMap.set(endRow, endCursor);

            const rowData = (entitiesData.investmentEntities?.edges ?? []).map(({ node }) => {
              const { complianceSummary: counts, positionCount } = node;
              const total = positionCount ?? 0;
              const compliant = counts?.compliantCount ?? 0;
              const inLPSubmission = counts?.lpSubmissionCount ?? 0;
              const inJSQReview = counts?.juniperSquareReviewCount ?? 0;
              const pendingDivestment = counts?.pendingDivestmentCount ?? 0;
              return {
                entityId: node.id,
                entityName: node.name,
                totalPositions: total,
                compliant,
                reviewNotStarted: Math.max(
                  0,
                  total - compliant - inLPSubmission - inJSQReview - pendingDivestment,
                ),
                inJSQReview,
                inLPSubmission,
                pendingDivestment,
                lastActivity: counts?.lastActivity,
              };
            });
            params.success({
              rowData,
              rowCount: entitiesData.investmentEntities?.count ?? undefined,
            });
          } catch {
            params.fail();
          }
        })();
      },
    };
  }, [executeEntities, arenaId]);
}
